import { useAddNativeClasses } from '@carvana/merch-native/hooks';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { legacySizes } from '~/constants/common/resizeBreakpoints';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import FinanceContext from '~/context/vdp/FinanceContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { useResizeListener, useTrackClickEvents } from '~/hooks/common';
import { useGetDiscountContext, useGetTrades } from '~/hooks/common/services';
import { useGetFavorites } from '~/hooks/vdp/favorites';
import useGetDeliveryContext from '~/hooks/vdp/services/useGetDeliveryContext';
import useGetSpinnerData from '~/hooks/vdp/services/useGetSpinnerData';
import useDirectDeliveryContext from '~/hooks/vdp/useDirectDeliveryContext'; // TODO: Can we remove this now?
import {
  useNativeMessageDispatcher,
  useNativeMessageListener
} from '~/native/vdp';
import {
  getCanShowDeliveryAndPickupInfo,
  getDeliveryPickup,
  getGeoLocation,
  getSoonestRoute,
  getUser,
  getVehicleId
} from '~/selectors';
import useSelector from '~/store/useSelector';
import { all } from '~/utilities/array';
import {
  getDeliveryPickupInfo,
  getHasOwnerReviews,
  getRecallStatus,
  getTransitCharge,
  getUserFinancing,
  getUserProfile,
  getUserPurchasing,
  getUserVehicleAvailability
} from './actions';
import {
  useEventDispatchers,
  useEventListeners,
  useGeolocation,
  useLogoutListener,
  usePageHash,
  useSendDataEvents
} from './hooks';

export default function VehicleDetailsData() {
  const { triggerUserFinancingRequest } = useContext(FinanceContext);
  const {
    availabilityUpdateFlag,
    favoriteUpdateFlag,
    logInFlag,
    triggerEvent
  } = useContext(GlobalContext);
  const dispatch = useDispatch();
  const getFavorites = useGetFavorites();
  const nativeDeviceType = useNativeDeviceType();
  const { state: stateAbbreviation = '', zip = '' } =
    useSelector(getGeoLocation);
  const canShowDeliveryAndPickup = useSelector(getCanShowDeliveryAndPickupInfo);
  const deliveryPickup = useSelector(getDeliveryPickup);
  const soonestRoute = useSelector(getSoonestRoute);
  const {
    isLoaded: isUserLoaded,
    isLoggedIn,
    userId = ''
  } = useSelector(getUser);
  const vehicleId = useSelector(getVehicleId);
  const reduxState = useSelector(state => state);
  const hasZip = Boolean(zip);

  useLogoutListener();
  usePageHash();
  useSendDataEvents();
  useTrackClickEvents();
  useResizeListener(legacySizes);

  useEventDispatchers();
  useEventListeners();
  useNativeMessageDispatcher();
  useNativeMessageListener();
  useAddNativeClasses(nativeDeviceType.isNative, nativeDeviceType.platform);

  useGetDeliveryContext();
  useDirectDeliveryContext();
  useGetDiscountContext(stateAbbreviation, userId, isUserLoaded);
  useGetTrades(isLoggedIn);
  useGeolocation(isLoggedIn);
  useGetSpinnerData();

  useEffectWithController(
    controller => {
      if (!hasZip) return;
      dispatch(getUserProfile(controller));
    },
    [dispatch, hasZip, logInFlag]
  );

  useEffect(() => {
    if (!isLoggedIn) return;
    triggerEvent.logIn();
  }, [isLoggedIn, triggerEvent]);

  useEffect(() => {
    if (!zip) return;
    dispatch(getUserVehicleAvailability({ userId, vehicleId, zip }));
  }, [availabilityUpdateFlag, dispatch, userId, vehicleId, zip]);

  useEffect(() => {
    if (!isUserLoaded || !isLoggedIn) return;
    dispatch(getUserPurchasing());
  }, [availabilityUpdateFlag, dispatch, isLoggedIn, isUserLoaded]);

  useEffect(() => {
    if (!all(isUserLoaded)) return;
    getFavorites();
  }, [favoriteUpdateFlag, getFavorites, isUserLoaded]);

  useEffect(() => {
    dispatch(getRecallStatus(vehicleId));
  }, [dispatch, vehicleId]);

  useEffectWithController(
    controller => {
      if (!all(canShowDeliveryAndPickup, zip)) return;

      dispatch(getDeliveryPickupInfo(userId, zip, controller));
    },
    [canShowDeliveryAndPickup, dispatch, userId, zip]
  );

  useEffectWithController(
    controller => {
      if (
        canShowDeliveryAndPickup &&
        deliveryPickup.isLoaded &&
        isUserLoaded &&
        stateAbbreviation &&
        vehicleId &&
        zip
      ) {
        dispatch(
          getTransitCharge(
            controller,
            isLoggedIn,
            stateAbbreviation,
            vehicleId,
            zip,
            soonestRoute?.routeId
          )
        );
      }
    },
    [
      canShowDeliveryAndPickup,
      deliveryPickup.isLoaded,
      dispatch,
      isLoggedIn,
      isUserLoaded,
      soonestRoute?.routeId,
      stateAbbreviation,
      vehicleId,
      zip
    ]
  );

  useEffect(() => {
    dispatch(getHasOwnerReviews);
  }, [dispatch]);

  useEffect(() => {
    if (!isUserLoaded) return;
    dispatch(getUserFinancing(userId));
  }, [dispatch, isUserLoaded, triggerUserFinancingRequest, userId]);

  useEffect(() => {
    if (typeof window.Cypress !== 'undefined') {
      window.reduxState = reduxState;
    }
  }, [reduxState]);

  return null;
}
